import { useQuery } from '@tanstack/react-query';
import { FieldHelperProps } from 'formik';
import { useCallback, useState } from 'react';

import ProductCardSimple from '../../product/ProductCardSimple';
import TopModalSection from '../common/TopModalSection';
import LoadingSkeletonHaggleForm from './LoadingSkeletonHaggleForm';

import PaginationBottomTable from '@/components/table/common/PaginationBottomTable';
import { IPaginationTable } from '@/components/table/common/interfaces/IPaginationTable';
import Button from '@/components/ui/button';
import {
	DEFAULT_PAGE_SIZE,
	PAGE_SIZE_OPTIONS,
} from '@/components/utils/constants';
import { ApiQueryKeys } from '@/interfaces/api/ApiQueryKeys';
import { ICollectionProduct } from '@/interfaces/api/product/ICollectionProduct';
import { IProduct } from '@/interfaces/api/product/IProduct';
import { haggleApiService } from '@/services/haggle.service';

type Props = {
	closeModal: () => void;
	resources: IProduct[];
	helpers: FieldHelperProps<unknown>;
	values: IProduct[];
	disabled: boolean;
};

const HaggleEventProductListModal = ({
	closeModal,
	helpers,
	values,
	disabled,
}: Props) => {
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [pageNumber, setPageNumber] = useState(1);
	const [pageBeforeCursor, setPageBeforeCursor] = useState<string | null>(null);
	const [pageCursor, setPageCursor] = useState<string | null>(null);

	const { data: products, isLoading: isFetching } =
		useQuery<ICollectionProduct>({
			queryKey: [
				ApiQueryKeys.getAllStoreProducts,
				pageSize,
				pageNumber,
				pageCursor,
				pageBeforeCursor,
			],
			queryFn: () =>
				haggleApiService.getAllStoreProducts({
					params: {
						'page[size]': pageSize,
						'page[number]': pageNumber,
						'page[cursor]': pageCursor,
						'page[beforeCursor]': pageBeforeCursor,
					},
				}),
			refetchOnWindowFocus: false,
		});

	const handlePageNumberChange = useCallback(
		(newPageNumber: number) => {
			if (!products) return;
			setPageNumber(newPageNumber);
			if (newPageNumber > pageNumber && products.endCursor) {
				// Going forward
				setPageBeforeCursor(null);
				setPageCursor(products.endCursor);
			} else if (newPageNumber < pageNumber && products.startCursor) {
				// Going backward
				setPageBeforeCursor(products.startCursor);
				setPageCursor(null);
			}
		},
		[products, pageNumber],
	);

	const handlePaginationOptions = (): IPaginationTable => {
		return {
			pageSize: products?.pageSize ?? pageSize,
			pageNumber: products?.pageNumber ?? pageNumber,
			pageCount:
				products?.pageCount ?? Math.ceil((products?.itemCount ?? 0) / pageSize),
			itemCount: products?.itemCount ?? 0,
			pageSizeOptions: PAGE_SIZE_OPTIONS,
			setPageSize,
			setPageNumber: handlePageNumberChange,
		};
	};
	const handleSelect = (selectedResource: IProduct) => {
		const isSelected = values.some((item) => item.id === selectedResource.id);
		if (isSelected) {
			const updatedValues = values.filter(
				(item) => item.id !== selectedResource.id,
			);
			helpers.setValue(updatedValues);
		} else {
			helpers.setValue([...values, selectedResource]);
		}
	};

	return (
		<div
			className="flex flex-col justify-center items-center"
			data-test="product-modal"
		>
			<TopModalSection
				className="gap-x-4 border-b py-2 w-full px-4"
				title={'Select products'}
				close={closeModal}
			/>
			<div className="px-4 w-full">
				{isFetching && <LoadingSkeletonHaggleForm />}
				{products && (
					<div className="flex justify-around h-[400px]">
						<div className="my-2 grid grid-cols-1 gap-y-4 sm:grid-cols-3 sm:gap-x-2 lg:grid-cols-6 xl:gap-x-2 overflow-y-auto grid-rows-custom-rows w-full">
							{products.data.map((product) => (
								<ProductCardSimple
									key={(product as IProduct).id}
									product={product as IProduct}
									isSelected={values.some(
										(item) => item.id === (product as IProduct).id,
									)}
									handleSelect={handleSelect}
									disabled={disabled}
								/>
							))}
						</div>
					</div>
				)}
				{products && <PaginationBottomTable {...handlePaginationOptions()} />}

				{!disabled && (
					<>
						<div className="border w-full" />
						<Button
							color="success"
							className="w-full my-2"
							shape="rounded"
							onClick={closeModal}
							data-test="bot-close-modal-button"
						>
							Return to form
						</Button>
					</>
				)}
			</div>
		</div>
	);
};

export default HaggleEventProductListModal;
